'use client';

import Icon from '@/components/data-display/icon';
import { LoadingSpinner, CalloutBanner } from '@alto-avios/alto-ui';
import { VARIANT } from '@/components/data-display/icon/icon.types';
import Button from '@/components/inputs/button';
import { VARIANT as BUTTON_VARIANT } from '@/components/inputs/button/button.types';
import PartnerTile from '@/components/integrated/partner-tile';
import { VARIANT as PartnerVariant } from '@/components/integrated/partner-tile/partner-tile.types';
import Modal, { closeModal, toggleModal } from '@/components/utilities/modal';
import { useFeatureTogglesContext } from '@/context/feature-toggles/feature-toggles-context';
import { FEATURE_TOGGLE_LIST } from '@/context/feature-toggles/feature-toggles.types';
import useSearch from '@/hooks/use-search';
import { DATA_IDS } from '@/types';
import { CustomLink as Link } from '@/components/custom-link';
import { useEffect, useRef } from 'react';
import useBasePath from '@/hooks/use-base-path';
import { formatHigherRate } from '@/utils/format-higher-rate';
import { formatWasRate } from '@/utils/format-was-rate';
import { useMicrocopyContext } from '@/context/microcopy/microcopy-context';
import trackEvent from '@/utils/track-event';
import { EVENT_NAME, USER_ACTION } from '@/utils/track-event/track-event.types';
import { getRetailerSlug } from '@/modules/track-clicks/track-clicks.utils';
import { useRouters } from '@/utils/app-router-migration/client';
import styles from './search-modal.module.scss';

export type SearchModalProperties = {
  modalLabel: string;
  wasLabel: string;
  upToLabel: string;
  fromLabel: string;
  title: string;
  closeButtonLabel: string;
  noResultsLabel: string;
};

const SearchModal = ({
  modalLabel,
  wasLabel,
  upToLabel,
  fromLabel,
  title,
  closeButtonLabel,
  noResultsLabel,
}: SearchModalProperties) => {
  const inputReference = useRef<HTMLInputElement>(null);
  const featureToggles = useFeatureTogglesContext();
  const disabledCLO = !featureToggles.includes(FEATURE_TOGGLE_LIST.CLO);
  const microcopy = useMicrocopyContext();
  const { push, query } = useRouters();
  const { basePath } = useBasePath();

  const {
    searchResults,
    searchValue,
    setSearchValue,
    debouncedValue,
    isLoading,
    errorPartnerResponse,
  } = useSearch(null, query?.search as string, disabledCLO);

  const goToSearch = () => {
    if (isLoading || errorPartnerResponse || searchResults?.length === 0)
      return;

    if (searchValue) {
      trackEvent({
        event: EVENT_NAME.SHOPPING_SEARCH,
        user_action: USER_ACTION.SEARCH_ENTER,
        search_query: searchValue,
        result_count: searchResults.length,
      });
      push(
        `${basePath}/retailers?search=${encodeURIComponent(searchValue)}#partner-search`,
      );
    } else push(`${basePath}/retailers`);

    toggleModal(modalLabel);
  };

  const handleOnKeyUp = ({ key }: React.KeyboardEvent<HTMLInputElement>) =>
    key === 'Enter' && goToSearch();

  const toggleSearchModal = () => {
    toggleModal(modalLabel);
    setSearchValue('');
  };

  const handleTileClick = (itemUrl: string) => {
    closeModal(modalLabel);
    if (getRetailerSlug(itemUrl))
      trackEvent({
        event: EVENT_NAME.SHOPPING_SEARCH,
        user_action: USER_ACTION.SEARCH_CLICK,
        search_query: searchValue,
        result_count: searchResults.length,
        retailerSlug: getRetailerSlug(itemUrl),
      });
  };

  useEffect(() => {
    if (
      !isLoading &&
      debouncedValue?.length > 1 &&
      !errorPartnerResponse &&
      searchValue !== query?.search
    ) {
      trackEvent({
        event: EVENT_NAME.SHOPPING_SEARCH,
        user_action: USER_ACTION.SEARCH_RESULTS,
        search_query: searchValue,
        result_count: searchResults.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, debouncedValue, searchResults, errorPartnerResponse]);

  return (
    <div className={styles[modalLabel]}>
      <Modal
        label={modalLabel}
        disableCloseButton
        closeOnOutsideClick
        onCloseFunction={() => setSearchValue('')}
        className={styles['search-modal__modal']}
      >
        <div className={styles['search-modal__header']}>
          <header className={styles['search-modal__search-bar']}>
            <Icon
              variant={VARIANT.Search}
              className={styles['search-modal__search-bar__icon']}
              onClick={goToSearch}
            />
            <label
              htmlFor="search-modal-input"
              className={styles['search-modal__visually-hidden-label']}
            >
              {title}
            </label>
            <input
              id="search-modal-input"
              ref={inputReference}
              value={searchValue}
              onChange={event => setSearchValue(event.target.value)}
              className={styles['search-modal__search-bar__input']}
              data-id={DATA_IDS.SEARCH_MODAL_INPUT}
              placeholder={title}
              onKeyUp={handleOnKeyUp}
              autoComplete="off"
            />
            <Button
              variant={BUTTON_VARIANT.Unstyled}
              onClick={toggleSearchModal}
              aria-label={closeButtonLabel}
              data-testid="close-button-id"
            >
              <Icon
                variant={VARIANT.X}
                className={styles['search-modal__search-bar__icon']}
              />
            </Button>
          </header>
        </div>
        <section className={styles['search-modal__content']}>
          {isLoading && (
            <div className={styles['search-modal__spinner']}>
              <LoadingSpinner size="2x" />
            </div>
          )}
          {!isLoading &&
            debouncedValue?.length > 1 &&
            searchResults.length === 0 &&
            !errorPartnerResponse && (
              <div className={styles['search-modal__no-results']}>
                {noResultsLabel}
              </div>
            )}

          {searchResults.length > 0 && (
            <div className={styles['search-modal__results']}>
              {searchResults.map(item => {
                const formattedRate = formatHigherRate(
                  item.rate,
                  upToLabel,
                  fromLabel,
                  item.noOfCollectionMethods,
                );
                const formattedWasRate = formatWasRate(item.wasRate, wasLabel);

                return (
                  <Link
                    key={item.id}
                    href={item.url as string}
                    className={styles['search-modal__results__tile']}
                    onClick={() => handleTileClick(item.url as string)}
                  >
                    <PartnerTile
                      key={item.id}
                      variant={PartnerVariant.Inline}
                      logo={{
                        src: item.logoUrl,
                        alt: item.name,
                      }}
                      name={item.name}
                      rate={formattedRate === '0' ? null : formattedRate}
                      wasRate={formattedWasRate}
                      showWasRate={Boolean(formattedWasRate)}
                    />
                  </Link>
                );
              })}
            </div>
          )}
          {errorPartnerResponse && (
            <div className={styles['search-modal__callout-banner']}>
              <CalloutBanner
                title={microcopy.general_errorsection_title}
                description={microcopy.general_errorsection_description}
                styleVariant="information"
                emphasis="secondary"
                data-id="section-tech-error"
              />
            </div>
          )}
        </section>
      </Modal>
    </div>
  );
};

export default SearchModal;
